/* ============================================================================
   Curated Collection JS  —  v2026-08-18
   Pins one product to the front of a collection grid, then fills the rest of
   the grid from a source collection.

   Changes from v2025-08-02-b:
   1. Handle list view renamed from "handles.json" to "handles". A period is
      not a valid character in a Shopify template suffix, so the old request
      silently fell back to the normal collection page (HTML), JSON parsing
      threw, and the rest of the grid came back empty.
   2. Added a native fallback to /collections/<handle>/products.json so the
      grid still fills even if the handles template is missing.
   3. Cache key version bumped so old bad responses are not reused.
   4. Added ?debug=1 logging so failures are visible in the browser console
      instead of failing silently.
============================================================================ */

(() => {
  const GRID_SEL = 'ul#product-grid, ul[id^="product-grid"], .product-grid';
  const CACHE_VERSION = 'v3';

  /* ------------------------------------------------------------------ */
  /* 0. Query-string parsing                                            */
  /* ------------------------------------------------------------------ */
  const qp = new URLSearchParams(location.search);
  const DEBUG = qp.get('debug') === '1';
  const log = (...args) => { if (DEBUG) console.log('[curated]', ...args); };

  let rawFirst = decodeURIComponent((qp.get('first') || '').trim());
  let firstHandle = '';
  let variantFromURL = '';

  if (rawFirst.startsWith('http')) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs[0] === 'products' && segs[1]) firstHandle = segs[1];
      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) {}
  } else {
    const segs = rawFirst.split('/').filter(Boolean);
    firstHandle = segs.pop();
  }
  if (!firstHandle) { log('no first= handle, exiting'); return; }

  /* variant param (digits only) */
  const variantID = (/^\d+$/.test(qp.get('variant') || variantFromURL))
    ? (qp.get('variant') || variantFromURL)
    : '';

  /* source collection */
  let source = (qp.get('source') || '').trim();
  if (!source) {
    const p = location.pathname.split('/').filter(Boolean);
    if (p[0] === 'collections' && p[1]) source = p[1];
  }
  if (!source) { log('no source collection, exiting'); return; }

  /* limit */
  const limit = Math.max(1, parseInt(qp.get('limit') || '24', 10));

  const grid = document.querySelector(GRID_SEL);
  if (!grid) { log('grid not found for selector', GRID_SEL); return; }

  log({ firstHandle, variantID, source, limit });

  /* ------------------------------------------------------------------ */
  /* 1. Theme-editor preview detection                                  */
  /* ------------------------------------------------------------------ */
  const previewExtras = (() => {
    const list = [];
    ['preview_theme_id', 'preview_theme_token', 'pb'].forEach(k => {
      if (qp.has(k)) list.push(`${k}=${encodeURIComponent(qp.get(k) || '')}`);
    });
    return list.join('&');
  })();
  const appendPreview = url =>
    previewExtras ? (url.includes('?') ? `${url}&${previewExtras}` : `${url}?${previewExtras}`) : url;
  const needsCookie = Boolean(previewExtras);

  /* ------------------------------------------------------------------ */
  /* 2. Helpers                                                         */
  /* ------------------------------------------------------------------ */
  const aspect = (grid.getAttribute('data-aspect') || '').toLowerCase();
  const view =
        aspect === 'square'   ? 'card-square'   :
        aspect === 'portrait' ? 'card-portrait' :
        'card';

  const cacheKey = (h, v) => `card:${CACHE_VERSION}:${view}:${h}:${v || 'noVar'}`;
  const getCache = (h, v) => { try { return sessionStorage.getItem(cacheKey(h, v)); } catch { return null; } };
  const setCache = (h, v, html) => { try { sessionStorage.setItem(cacheKey(h, v), html); } catch {} };

  const fetchCard = async (handle, variant = '') => {
    const cached = getCache(handle, variant);
    if (cached) return cached;

    const url = appendPreview(
      `/products/${encodeURIComponent(handle)}?view=${view}${variant ? `&variant=${variant}` : ''}`
    );
    const r = await fetch(url, {
      credentials: needsCookie ? 'same-origin' : 'omit',
      cache: 'force-cache'
    });
    if (!r.ok) { log('card fetch failed', handle, r.status); return ''; }
    const html = await r.text();

    /* A missing product.<view>.liquid template returns the full product page,
       which would blow up the grid. Only accept a real card fragment. */
    if (/<html/i.test(html)) {
      log('card view template missing, got full page for', handle, 'view=' + view);
      return '';
    }

    if (html.trim()) setCache(handle, variant, html);
    return html;
  };

  const render = html => { if (html.trim()) grid.insertAdjacentHTML('beforeend', html); };
  const updateCount = () => {
    const c = grid.querySelectorAll('.grid__item').length;
    document.querySelectorAll('[data-product-count], .product-count, .collection-product-count')
      .forEach(el => el.textContent = `${c} Products`);
  };

  /* ------------------------------------------------------------------ */
  /* 3. Handle list: custom view first, native products.json as fallback */
  /* ------------------------------------------------------------------ */
  const fetchHandlesFromView = async () => {
    const url = appendPreview(`/collections/${encodeURIComponent(source)}?view=handles`);
    const r = await fetch(url, {
      credentials: needsCookie ? 'same-origin' : 'omit',
      cache: 'no-cache'
    });
    if (!r.ok) { log('handles view HTTP', r.status); return []; }
    const text = await r.text();
    try {
      const data = JSON.parse(text.trim());
      const list = Array.isArray(data?.handles) ? data.handles : [];
      log('handles view returned', list.length);
      return list;
    } catch (_) {
      log('handles view did not return JSON. Is templates/collection.handles.liquid installed?');
      return [];
    }
  };

  const fetchHandlesFromProductsJson = async () => {
    const url = `/collections/${encodeURIComponent(source)}/products.json?limit=250`;
    const r = await fetch(url, { credentials: 'omit', cache: 'no-cache' });
    if (!r.ok) { log('products.json HTTP', r.status); return []; }
    const data = await r.json().catch(() => null);
    const list = Array.isArray(data?.products) ? data.products.map(p => p.handle) : [];
    log('products.json returned', list.length);
    return list;
  };

  const fetchHandles = async () => {
    let list = await fetchHandlesFromView().catch(() => []);
    if (!list.length) list = await fetchHandlesFromProductsJson().catch(() => []);
    return list;
  };

  /* ------------------------------------------------------------------ */
  /* 4. Skeleton + parallel fetches                                     */
  /* ------------------------------------------------------------------ */
  grid.innerHTML = '<li class="grid__item">Loading…</li>';

  const pinnedPromise  = fetchCard(firstHandle, variantID);
  const handlesPromise = fetchHandles();

  /* ------------------------------------------------------------------ */
  /* 5. Render + variant handling                                       */
  /* ------------------------------------------------------------------ */
  (async () => {
    /* 5-a  pinned */
    const pinnedHTML = await pinnedPromise;
    grid.innerHTML = '';
    render(pinnedHTML);

    /* 5-b  rest */
    const raw = await handlesPromise;

    const seen = new Set([firstHandle]);
    const restHandles = [];
    const room = Math.max(0, limit - (pinnedHTML ? 1 : 0));
    for (const h of raw) {
      if (h && !seen.has(h)) { seen.add(h); restHandles.push(h); }
      if (restHandles.length >= room) break;
    }
    log('rendering rest', restHandles.length, 'of', raw.length);

    const cards = await Promise.all(restHandles.map(h => fetchCard(h)));
    cards.forEach(render);
    updateCount();

    /* 5-c  swap pinned image and update links if a variant was specified */
    if (variantID) {
      try {
        const prod = await fetch(`/products/${firstHandle}.js`).then(r => r.json());
        const varObj = prod.variants.find(v => String(v.id) === variantID);
        if (!varObj || !varObj.featured_image) return;

        const newSrcBase = varObj.featured_image.src;
        const pinnedLi = grid.querySelector('li.grid__item:first-child');
        if (!pinnedLi) return;

        /* swap imgs */
        pinnedLi.querySelectorAll('img').forEach(img => {
          const m = img.src.match(/_(\d+x)\./);
          const sized = m ? newSrcBase.replace(/\.jpg|\.png/i, `_${m[1]}.jpg`) : newSrcBase;
          img.src = sized;
          img.srcset = '';
        });

        /* update all anchors to include ?variant=ID */
        pinnedLi.querySelectorAll('a[href*="/products/"]').forEach(a => {
          const u = new URL(a.href, location.origin);
          u.searchParams.set('variant', variantID);
          a.href = u.pathname + u.search;
        });
      } catch (err) { log('variant swap failed', err); }
    }
  })().catch(err => {
    log('fatal', err);
    grid.innerHTML =
      '<li class="grid__item">Failed to load curated list. Check handle/variant/source.</li>';
  });
})();
